import axios from '@/axiosInterceptor';
import {
  defaultRateConQueryTabLookup,
  rateConSortLookup,
  rateConFilterLookup,
  rateConStatusesLookup,
} from '@/utils/rateConFiltersLookup';

const buildFilterParams = (query, userId) => {
  let filterCount = 0;
  let params = '';
  for (const key in query) {
    const filterParam = rateConFilterLookup[key];
    const value = query[key];
    if (
      key === 'carrierIdType' ||
      key === 'sort' ||
      key === 'page' ||
      key === 'creatorName'
    ) {
      continue;
    }
    if (key === 'carrierId') {
      filterCount++;
      params += rateConFilterLookup[query.carrierIdType || 'mc'].query + value;
      continue;
    }
    if (key === 'tab' && value === 'my') {
      params += `&q[creator_id_eq]=${userId}`;
      continue;
    }
    if (key.includes('status')) {
      filterCount++;
      const status = rateConStatusesLookup[key.split('-')[0]];
      params += status?.query || '';
      continue;
    }
    if (value && filterParam) {
      filterCount++;
      params += filterParam.query + value;
    }
  }
  return { filterCount: filterCount, params: params };
};

const formatPagination = ({
  currentpage = 1,
  totalpages = 0,
  totalitems = 0,
}) => ({
  currentPage: Number(currentpage),
  totalPages: Number(totalpages),
  totalRecords: Number(totalitems),
});

const state = () => ({
  rates: [],
  rates_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  flagged_rates: [],
  flagged_rates_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  viewed_rates: [],
  viewed_rates_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  delivered_rates: [],
  delivered_rates_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  rate_con_emails: [],
  rate_con_emails_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  flagged_rate_con_emails: [],
  flagged_rate_con_emails_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  undelivered_rate_con_emails: [],
  undelivered_rate_con_emails_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  delivered_rate_con_emails: [],
  delivered_rate_con_emails_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  current_rate: null,
  current_rate_terms: null,
  current_rate_con_email: null,
  rateConSearchTerm: null,
  all: {
    controller: null,
    hasLoaded: false,
    isLoading: false,
    rates: [],
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
    },
    activeFiltersCount: 0,
    query: defaultRateConQueryTabLookup.all,
  },
  my: {
    controller: null,
    hasLoaded: false,
    isLoading: false,
    rates: [],
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
    },
    activeFiltersCount: 0,
    query: defaultRateConQueryTabLookup.my,
  },
});

// getters
const getters = {};

// actions
const actions = {
  async getAllRates({ commit, rootState, state }, tab = 'all') {
    const query = state[tab].query;
    const { page = 1, sort = 'created_at_desc' } = query;
    const sortParam = rateConSortLookup[sort]?.query || 'created_at+desc';
    const userId = rootState.users.currentUser?.id;
    const filterParams = buildFilterParams(query, userId);
    commit('setFilterCount', { tab: tab, value: filterParams.filterCount });
    const url = `${process.env.API_MONITOR}/api/v1/rate_confirmations?${filterParams.params}&q[s]=${sortParam}&page=${page}&per_page=50`;
    const previousRequest = state[tab].controller;
    if (previousRequest) {
      previousRequest.abort();
      commit('setController', { tab: tab, value: null });
    }
    const controller = new AbortController();
    commit('setHasLoaded', { tab: tab, value: false });
    commit('setIsLoading', { tab: tab, value: true });
    commit('setController', { tab: tab, value: controller });
    try {
      const response = await axios.get(url, {
        signal: controller.signal,
      });
      if (response?.data && response?.headers?.currentpage) {
        commit('setQuery', { tab: tab, value: query });
        commit('setController', { tab: tab, value: null });
        commit('setIsLoading', { tab: tab, value: false });
        commit('setHasLoaded', { tab: tab, value: true });
        commit('setRateCons', { tab: tab, value: response.data });
        commit('setPagination', {
          tab: tab,
          value: formatPagination(response.headers),
        });
        return response;
      }
      if (response) {
        throw new Error('No data returned from API', { cause: response });
      }
    } catch (error) {
      console.error(error);
      commit('setIsLoading', { tab: tab, value: false });
      commit('setHasLoaded', { tab: tab, value: true });
      commit('setRateCons', { tab: tab, value: [] });
      commit('setPagination', {
        tab: tab,
        value: {
          currentPage: 1,
          totalPages: 0,
          totalRecords: 0,
        },
      });
      return error;
    }
  },
  async getViewedRates({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[creator_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations?q[s]=id+desc${userParam}${searchParam}&q[status_eq]=viewed&page=${currentPage}&per_page=50`
      );
      commit('setViewedRates', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getFlaggedRates({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[creator_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations?q[s]=id+desc${userParam}${searchParam}&q[with_open_tickets]=true&page=${currentPage}&per_page=50`
      );
      commit('setFlaggedRates', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getDeliveredRates({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[creator_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations?q[s]=id+desc${userParam}${searchParam}&q[status_eq]=delivered&page=${currentPage}&per_page=50`
      );
      commit('setDeliveredRates', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getRateDetail({ commit }, id) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations/${id}`
      );
      commit('setCurrentRate', res?.data);
      return res?.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getRateTerms({ commit }, id) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations/${id}/get_terms`
      );
      commit('setCurrentRateTerms', res?.data);
      return res?.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getAllRateConEmails({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[user_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmation_mails?q[s]=id+desc${userParam}${searchParam}&page=${currentPage}&per_page=50`
      );
      commit('setAllRateConEmails', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getFlaggedRateConEmails({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[user_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmation_mails?q[s]=id+desc&q[status_eq]=flagged${userParam}${searchParam}&page=${currentPage}&per_page=50`
      );
      commit('setFlaggedRateConEmails', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getUndeliveredRateConEmails({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[user_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmation_mails?q[s]=id+desc&q[status_not_in][]=flagged&q[status_not_in][]=completed${userParam}${searchParam}&page=${currentPage}&per_page=50`
      );
      commit('setUndeliveredRateConEmails', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getDeliveredRateConEmails({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[user_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmation_mails?q[s]=id+desc&q[status_eq]=completed${userParam}${searchParam}&page=${currentPage}&per_page=50`
      );
      commit('setDeliveredRateConEmails', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getRateConEmailDetail({ commit }, id) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmation_mails/${id}`
      );
      commit('setCurrentRateConEmail', res?.data);
      return res?.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async cancelRateConfirmation({ commit }, id) {
    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations/${id}/mark_as_cancelled`
      );
      if (res.status !== 403) {
        commit('setCurrentRate', res?.data);
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [422].includes(e?.response?.status)) {
        return e;
      } else {
        throw e;
      }
    }
  },
  async rejectRateConfirmation({ commit }, id) {
    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations/${id}/mark_as_rejected`
      );

      if (res.status !== 403 && res?.data) {
        commit('setCurrentRate', res?.data);
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [422].includes(e?.response?.status)) {
        return e;
      } else {
        throw e;
      }
    }
  },
  async showOnlyMyRateCons({ commit }, val) {
    commit('setOnlyMyRateCons', val);
  },
  async saveRateConSearchTerm({ commit }, val) {
    commit('setRateConSearchTerm', val);
  },
};

// mutations
const mutations = {
  setFilterCount(state, { tab, value }) {
    state[tab].activeFiltersCount = value;
  },
  setController(state, { tab, value }) {
    state[tab].controller = value;
  },
  setHasLoaded(state, { tab, value }) {
    state[tab].hasLoaded = value;
  },
  setIsLoading(state, { tab, value }) {
    state[tab].isLoading = value;
  },
  setRateCons(state, { tab, value }) {
    state[tab].rates = value;
  },
  setPagination(state, { tab, value }) {
    state[tab].pagination = value;
  },
  setQuery(state, { tab, value }) {
    state[tab].query = value;
  },
  setRates(state, res) {
    const headers = res?.headers;
    state.rates_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.rates = res?.data;
  },
  setFlaggedRates(state, res) {
    const headers = res?.headers;
    state.flagged_rates_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.flagged_rates = res?.data;
  },
  setViewedRates(state, res) {
    const headers = res?.headers;
    state.viewed_rates_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.viewed_rates = res?.data;
  },
  setDeliveredRates(state, res) {
    const headers = res?.headers;
    state.delivered_rates_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.delivered_rates = res?.data;
  },
  setAllRateConEmails(state, res) {
    const headers = res?.headers;
    state.rate_con_emails_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.rate_con_emails = res?.data;
  },
  setFlaggedRateConEmails(state, res) {
    const headers = res?.headers;
    state.flagged_rate_con_emails_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.flagged_rate_con_emails = res?.data;
  },
  setUndeliveredRateConEmails(state, res) {
    const headers = res?.headers;
    state.undelivered_rate_con_emails_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.undelivered_rate_con_emails = res?.data;
  },
  setDeliveredRateConEmails(state, res) {
    const headers = res?.headers;
    state.delivered_rate_con_emails_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.delivered_rate_con_emails = res?.data;
  },
  setCurrentRate(state, data) {
    state.current_rate = data;
  },
  setCurrentRateTerms(state, data) {
    state.current_rate_terms = data;
  },
  setOnlyMyRateCons(state, bool) {
    state.onlyMyRateCons = bool;
  },
  setRateConSearchTerm(state, term) {
    state.rateConSearchTerm = term;
  },
  setCurrentRateConEmail(state, data) {
    state.current_rate_con_email = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
